import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { v4 as uuid4 } from 'uuid';
import { buildConfig } from './auth-config';
import { AppConfig } from '../app.config';
import { CommonHelper } from './common.helper';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';

const DmpRegions = ['1081', '1082', '1083', '1084', '1085', '400'];

@Injectable()
export class AuthService {
    private readonly authHeaderName: string = 'Authorization';
    private readonly authDataKey: string = 'authData';
    private readonly lastPageUrlKey = 'lastPageUrl';

    constructor(private readonly router: Router, protected readonly oauth: OAuthService) {

    }

    public get isLoggedIn(): boolean {
        return this.oauth.hasValidIdToken() && this.oauth.hasValidAccessToken();
    }

    protected getConfig(): AuthConfig {
        return buildConfig({
            clientId: AppConfig.authClientId,
            issuer: AppConfig.authAuthority,
        });
    }

    public async initialize(): Promise<void> {
        this.oauth.configure(this.getConfig());

        await this.oauth.loadDiscoveryDocument();
        await this.oauth.tryLogin();

        if (!this.isLoggedIn) {
            this.oauth.initCodeFlow();
        } else {
            const lastPageUrl = this.getLastPageUrl();
            if (lastPageUrl) {
                sessionStorage.removeItem(this.lastPageUrlKey);
                this.router.navigateByUrl(lastPageUrl);
            }
        }
    }

    public isClaimsValid(): boolean {
        const regionCode = this.getRegionCode();
        const organizationIdentifier = this.getOrganizationIdentifier();
        const roles = this.getUserRoles();

        // only Operator with admin role can have full access
        // others regardsless if the have a RegionsKode with admin role alone will be access denied
        if (roles === AppConfig.dmpAdminRole && organizationIdentifier !== AppConfig.operatorCVR) {
            return false;
        }

        if (regionCode) {
            return true;
        }

        // 1) if we have a mandatory claim dk:gov:saml:attribute:CvrNumberIdentifier
        // and it is not present it should lead to an access denied.
        if (!organizationIdentifier) {
            return false;
        }

        // 3) If we get an regioncode and a role and organizationidentifier - then is it also a case of access denied
        if (regionCode && roles && organizationIdentifier) {
            return false;
        }

        return true;
    }

    public updateAuthHeader(headers: HttpHeaders): void {
        headers.append(this.authHeaderName, this.getTokenType() + ' ' + this.getAccessToken());
    }

    public getAccessToken(): string {
        return this.oauth.getAccessToken();
    }

    public getIdToken(): string {
        return this.oauth.getIdToken();
    }

    public getTokenType(): string {
        return this.oauth.getIdToken() ? 'Bearer' : '';
    }

    public getAuthData(): any {
        const data = sessionStorage.getItem(this.authDataKey);
        if (data) {
            return JSON.parse(data);
        }
        return {};
    }

    public removeAuthData(): void {
        sessionStorage.removeItem(this.authDataKey);
        sessionStorage.removeItem(this.lastPageUrlKey);
    }

    public redirectToLastPage(): void {
        const lastPageUrl = this.getLastPageUrl();

        this.router.navigate([lastPageUrl]);
    }

    private getLastPageUrl(): string {
        let lastPageUrl = sessionStorage.getItem(this.lastPageUrlKey);
        if (lastPageUrl === 'access-denied' ||  lastPageUrl === '/access-denied') {
            sessionStorage.removeItem(this.lastPageUrlKey);
            lastPageUrl = '';
        }

        lastPageUrl = lastPageUrl ? lastPageUrl : '';
        return lastPageUrl;
    }

    public setLastPageUrl(lastPageUrl: string): void {
        sessionStorage.setItem(this.lastPageUrlKey, lastPageUrl);
    }

    public logout(): void {
        this.removeAuthData();
        this.oauth.logOut();
    }

    public getUserInfo(): any {
        const token = this.getAccessToken();
        if (!token) {
            return {};
        }
        const userInfo = CommonHelper.decodeAuthToken(token);
        return userInfo;
    }

    public getUserRoles(): any {
        const userInfo = this.getUserInfo();
        return userInfo['http://www.miljoeportal.dk/roles'];
    }

    public isRoleValid(): boolean {
        const roles = this.getUserRoles();
        if (!roles) {
            return false;
        }
        const validRoles = [...AppConfig.fullAccessRoles, ...AppConfig.allowedRoles];
        return validRoles.some(role => roles.includes(role));
    }

    public isFullAccessUser(): boolean {
        const isExternal = this.isExternalUser();
        if (!!isExternal) {
            return false;
        }
        const roles = this.getUserRoles();
        if (!roles) {
            return false;
        }
        const index = AppConfig.fullAccessRoles.findIndex(p => roles.indexOf(p) >= 0);
        return index >= 0;
    }

    public isOperator(): boolean {
        const cvrCode = this.getOrganizationIdentifier();
        const roles = this.getUserRoles();
        let isContainAdminRole = false;
        if  (Array.isArray(roles)) {
            isContainAdminRole = roles.findIndex(p => AppConfig.fullAccessRoles[1].indexOf(p) >= 0) >= 0;
        } else {
            isContainAdminRole = AppConfig.dmpAdminRole === roles;
        }

        return isContainAdminRole && cvrCode === AppConfig.operatorCVR;
    }

    public isExternalUser(): boolean {
        const region = this.getRegionCode();
        return DmpRegions.indexOf(region) < 0;
    }

    public getUserEmail(): string {
        const userInfo = this.getUserInfo();
        return userInfo['urn:oid:0.9.2342.19200300.100.1.3'];
    }

    public getUserPrincipalName(): string {
        const userInfo = this.getUserInfo();
        return userInfo['http://www.miljoeportal.dk/userPrincipalName'];
    }

    public getRegionCode(): string {
        const userInfo = this.getUserInfo();
        return userInfo['http://schemas.xmlsoap.org/claims/RegionsKode'];
    }

    public tryGetUserRegionCode(): string {
        if (!this.isExternalUser()) {
            return this.getRegionCode();
        }

        return this.getOrganizationIdentifier();
    }

    public getOrganizationIdentifier(): string {
        const userInfo = this.getUserInfo();
        return userInfo['dk:gov:saml:attribute:CvrNumberIdentifier'];
    }

    public randomId(): string {
        const u = uuid4();
        return u.replace(/-/g, '');
    }

    public parseLocationHashAccessToken(): any {
        if (window.location.hash.indexOf('#') === 0 && window.location.hash.indexOf('access_token=') >= 0) {
            return this.parseQueryString(window.location.hash.substr(1));
        }
        return null;
    }

    private parseQueryString(queryString): any {
        const data = {} as any;

        if (queryString === null) {
            return data;
        }

        const pairs = queryString.split('&');

        for (const pair of pairs) {
            const separatorIndex = pair.indexOf('=');
            let escapedKey = null;
            let escapedValue = null;
            if (separatorIndex === -1) {
                escapedKey = pair;
            } else {
                escapedKey = pair.substr(0, separatorIndex);
                escapedValue = pair.substr(separatorIndex + 1);
            }

            const key = decodeURIComponent(escapedKey);
            const value = decodeURIComponent(escapedValue);

            data[key] = value;
        }

        return data;
    }
}
